import type {
  Stripe,
  StripeElements,
  StripeError,
  StripePaymentElement,
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
  StripePaymentElementSavedPaymentMethodUpdateEvent,
} from '@stripe/stripe-js';

import { STRIPE_ELEMENT_ID } from './constants';
import type {
  CreatePaymentMethodResult,
  ElementsSubmitResult,
  PaymentMethodChangeEvent,
  StripeElementsConfig,
} from './types';

/**
 * Wrapper for Stripe Elements that encapsulates all Stripe-specific functionality
 * and provides a clean interface to the application layer without exposing Stripe types
 */
type StripePaymentElementWithSavedMethodUpdateOff = StripePaymentElement & {
  off(
    eventType: 'savedpaymentmethodupdate',
    handler?: (event: StripePaymentElementSavedPaymentMethodUpdateEvent) => unknown
  ): StripePaymentElement;
};

export class StripeElementsFacade {
  private elements: StripeElements | null = null;

  private paymentElement: StripePaymentElement | null = null;

  private changeListeners: ((event: PaymentMethodChangeEvent) => void)[] = [];

  private cleanUpPaymentElementListeners = () => undefined;

  constructor(private stripe: Stripe, private getPaymentElement: () => HTMLElement) {}

  /**
   * Create Stripe Elements with the given configuration
   *
   * @see https://docs.stripe.com/api/payment_methods/object#payment_method_object-type
   */
  public async createAndMountPaymentElement(
    elementsOptions: StripeElementsConfig,
    paymentOptions?: StripePaymentElementOptions
  ): Promise<void> {
    this.elements = this.stripe.elements(elementsOptions);

    this.paymentElement = this.elements.create('payment', paymentOptions);

    // Set up payment element listeners
    const changeHandler = (event: StripePaymentElementChangeEvent) => {
      const legacyPaymentMethod = (event.value as unknown as { paymentMethod?: { id?: string; type?: string } })
        .paymentMethod;

      const paymentMethodType =
        event.value?.type || event.value?.payment_method?.type || legacyPaymentMethod?.type || '';
      const paymentMethodId = event.value?.payment_method?.id || legacyPaymentMethod?.id;

      const changeEvent: PaymentMethodChangeEvent = {
        paymentMethodType,
        paymentMethodId,
        complete: event.complete,
        empty: event.empty,
      };

      this.changeListeners.forEach((listener) => listener(changeEvent));
    };

    const savedPaymentMethodUpdateHandler = (event: StripePaymentElementSavedPaymentMethodUpdateEvent) => {
      const legacyPaymentMethod = (event as unknown as { paymentMethod?: { id?: string; type?: string } })
        .paymentMethod;
      const paymentMethod = event.payment_method || legacyPaymentMethod;

      const changeEvent: PaymentMethodChangeEvent = {
        paymentMethodType: paymentMethod?.type || '',
        paymentMethodId: paymentMethod?.id,
        complete: event.success,
      };

      this.changeListeners.forEach((listener) => listener(changeEvent));
    };

    this.paymentElement.on('change', changeHandler);
    this.paymentElement.on('savedpaymentmethodupdate', savedPaymentMethodUpdateHandler);

    this.cleanUpPaymentElementListeners = () => {
      this.paymentElement?.off('change', changeHandler);
      (this.paymentElement as StripePaymentElementWithSavedMethodUpdateOff | null)?.off(
        'savedpaymentmethodupdate',
        savedPaymentMethodUpdateHandler
      );
    };

    this.paymentElement.mount(this.getPaymentElement());
  }

  /**
   * Update the Elements configuration (for price changes, etc.)
   */
  public async updateElements(config: Partial<StripeElementsConfig>): Promise<void> {
    this.elements?.update(config);
  }

  /**
   * Add a listener for payment method changes
   */
  public addChangeListener(listener: (event: PaymentMethodChangeEvent) => void): void {
    this.changeListeners.push(listener);
  }

  /**
   * Remove a change listener
   */
  public removeChangeListener(listener: (event: PaymentMethodChangeEvent) => void): void {
    this.changeListeners = this.changeListeners.filter((l) => l !== listener);
  }

  /**
   * Submit the Elements form
   */
  public async submit({
    returnUrl,
    countryCode,
    postalCode,
    fullName,
    email,
    phone,
  }: {
    returnUrl: string;
    countryCode: string;
    postalCode: string;
    fullName: string;
    email: string;
    phone: string;
  }): Promise<ElementsSubmitResult> {
    if (!this.elements) {
      throw new Error('Elements must be created before submitting');
    }

    const result = (await this.stripe.confirmSetup({
      elements: this.elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          allow_redisplay: 'always',
          billing_details: {
            name: fullName,
            email,
            phone,
            address: {
              postal_code: postalCode,
              country: countryCode,
            },
          },
        },
      },
    })) as unknown as { error: StripeError } | { setupIntent: { payment_method: string } };

    return {
      error:
        'error' in result
          ? {
              message: result.error.message || 'Unknown error',
              type: result.error.type,
            }
          : undefined,
      paymentMethodId: 'setupIntent' in result ? result.setupIntent.payment_method : undefined,
    };
  }

  /**
   * Scroll the payment element into view, used when there are validation errors to ensure the user sees the error messages
   */
  public scrollIntoView(): void {
    const stripeElement = document.getElementById(STRIPE_ELEMENT_ID);

    if (stripeElement) {
      stripeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public async getSelectedPaymentMethod(): Promise<string | null> {
    if (!this.elements) {
      throw new Error('Elements must be created before getting selected payment method');
    }

    const result = await this.elements.submit();

    if ('error' in result) {
      throw result.error;
    }

    return result.selectedPaymentMethod ?? null;
  }

  /**
   * Create a payment method from the current Elements state
   */
  public async createPaymentMethod(): Promise<CreatePaymentMethodResult> {
    if (!this.elements) {
      throw new Error('Elements must be created before creating payment method');
    }

    const result = await this.stripe.createPaymentMethod({
      elements: this.elements,
    });

    return {
      paymentMethod: result.paymentMethod
        ? {
            id: result.paymentMethod.id,
            type: result.paymentMethod.type,
          }
        : undefined,
      error: result.error
        ? {
            message: result.error.message || 'Unknown error',
            type: result.error.type,
          }
        : undefined,
    };
  }

  public async fetchElementUpdates() {
    await this.elements?.fetchUpdates();
  }

  public async retrieveSetupIntent() {
    const clientSecret = new URLSearchParams(window.location.search).get('setup_intent_client_secret');

    if (!clientSecret) {
      throw new Error('setup_intent_client_secret is not present in the URL');
    }

    const { setupIntent } = await this.stripe.retrieveSetupIntent(clientSecret);

    if (!setupIntent) {
      throw new Error('setup_intent_client_secret is not present in the URL');
    }

    switch (setupIntent.status) {
      case 'succeeded': {
        return {
          status: 'succeeded' as const,
        };
      }

      case 'processing': {
        return {
          status: 'processing' as const,
        };
      }

      case 'requires_payment_method': {
        return {
          status: 'requires_payment_method' as const,
        };
      }

      default: {
        return null;
      }
    }
  }

  /**
   * Unmount the payment element
   */
  public unmountElement(): void {
    this.cleanUpPaymentElementListeners();
    this.cleanUpPaymentElementListeners = () => undefined;
    this.paymentElement?.unmount();
  }

  /**
   * Clean up all resources
   */
  public destroy(): void {
    this.unmountElement();
    this.paymentElement?.destroy();
    this.changeListeners = [];
    this.elements = null;
    this.paymentElement = null;
  }

  /**
   * Check if elements are ready for use
   */
  public isReady(): boolean {
    return this.elements !== null && this.paymentElement !== null;
  }
}
